/**
 * @fileoverview PHARMAKON — API « Alertes équipe » (canal titulaire → équipe).
 * @module services/alerteEquipe.api
 *
 * PLAN_mobile_pharma_owner_manager §4.2.
 *
 * Monté sur le client axios partagé (`api.client.js`, base `/api/v1`).
 * L'intercepteur de réponse rend DÉJÀ `res.data` : chaque appel résout donc
 * directement le corps du contrôleur, qu'on déballe comme `confrere.api.js`.
 *
 * NAMESPACE — le préfixe est `/alertes-equipe`, JAMAIS `/comms` ni
 * `/notifications`. `/comms` porte le `Broadcast` PLATEFORME
 * (ADMIN_PHARMAKON → officines) ; celui-ci est le canal EMPLOYEUR → EMPLOYÉ
 * d'UNE officine. Deux tables, deux plans de confidentialité (§3.1, §5.6).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POURQUOI CE CLIENT N'ENVOIE PAS `If-None-Match` — écart assumé (§3.4).
 *
 * Le serveur pose bien un `ETag` faible sur `/pending-count` et sait rendre un
 * `304`. Mais l'intercepteur de réponse PARTAGÉ (`api.client.js:193`) rend
 * `res.data` : le STATUT et les EN-TÊTES sont perdus avant d'arriver ici. Un
 * `304` (sans corps) serait donc indiscernable d'un ensemble en attente VIDE —
 * et un ensemble vide FAIT DISPARAÎTRE les alertes affichées. Sur un canal dont
 * le seul métier est de ne jamais perdre une alerte urgente, c'est un défaut de
 * correction, pas une micro-optimisation manquée.
 *
 * On compare donc `data.version`, qui est EXACTEMENT la clé que l'ETag
 * enveloppe (`W/"ae-<version>"`, voir `alerte-equipe.controller.js`) : la
 * détection de changement est identique au bit près. Seuls ~150 octets de corps
 * par sondage sont renoncés. Le jour où le client partagé exposera la réponse
 * complète, réactiver le `If-None-Match` ici est un changement d'une ligne.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import api from "./api.client.js";

const BASE = "/alertes-equipe";
const unwrap = (r) => (r && typeof r === "object" && "success" in r ? r.data : r);

// ─── Consommation (tout rôle d'équipe) ───────────────────────────────────────

/**
 * Le sondage CHAUD. Rend le bloc
 * `{ info, important, urgent, total, max_niveau, version, poll_ms, … }`.
 * La comparaison de `version` avec celle du sondage précédent dit s'il faut
 * recharger `/actives` — voir la note d'en-tête sur l'ETag.
 */
export const compteur = () => api.get(`${BASE}/pending-count`).then(unwrap);

/**
 * Le flux d'affichage complet — estampille `delivered_at` côté serveur.
 *
 * ATTENTION : le contrôleur étale la réponse (`{ success, data, version,
 * poll_ms, … }`), la cadence est donc SŒUR de la liste, pas dedans. On ne
 * déballe pas vers `data` ici, sinon on jetterait la cadence que le client doit
 * honorer (L-2 — le client n'invente aucun délai).
 */
export const actives = () =>
  api.get(`${BASE}/actives`).then((r) => {
    const { success: _ignore, ...reste } = r ?? {};
    return { data: [], ...reste };
  });

/** « Vu » — le rejet explicite d'un bandeau ou d'un toast. Idempotent. */
export const marquerVu = (id) => api.post(`${BASE}/${id}/vu`).then(unwrap);

/** Accusé de réception explicite (palier URGENT). Idempotent, audité. */
export const accuser = (id, source = "web") =>
  api.post(`${BASE}/${id}/accuser`, { source }).then(unwrap);

// ─── Rédaction (titulaire / manager) ─────────────────────────────────────────

/** Crée un BROUILLON. Rien n'est diffusé à ce stade. */
export const creer = (dto) => api.post(BASE, dto).then(unwrap);

/**
 * Ma boîte d'envoi, paginée. Le contrôleur étale ici aussi
 * (`{ success, data, total, page, limit }`) — on garde l'enveloppe.
 */
export const lister = (params = {}) =>
  api.get(BASE, { params }).then((r) => {
    const { success: _ignore, ...reste } = r ?? {};
    return { data: [], total: 0, page: 1, ...reste };
  });

/** Le détail d'une alerte + ses compteurs. */
export const detail = (id) => api.get(`${BASE}/${id}`).then(unwrap);

/** Modifier un BROUILLON (409 une fois envoyée — on corrige par réémission). */
export const modifier = (id, dto) => api.patch(`${BASE}/${id}`, dto).then(unwrap);

/** Supprimer un BROUILLON (409 une fois envoyée). */
export const supprimer = (id) => api.delete(`${BASE}/${id}`).then(unwrap);

/** Aperçu de l'audience — compte les cibles SANS rien écrire. */
export const apercu = (id) => api.get(`${BASE}/${id}/preview`).then(unwrap);

/** Diffuser. 409 si déjà envoyée, 422 audience vide, 429 plafond URGENT. */
export const envoyer = (id, user_ids = undefined) =>
  api.post(`${BASE}/${id}/send`, user_ids ? { user_ids } : {}).then(unwrap);

/** L'interrupteur : retire l'alerte de l'affichage au sondage suivant. */
export const annuler = (id) => api.post(`${BASE}/${id}/annuler`).then(unwrap);

/** Corriger un envoi = réémettre (L-1), jamais éditer. */
export const reemettre = (id, dto = {}) =>
  api.post(`${BASE}/${id}/reissue`, dto).then(unwrap);

// ─── Le tableau d'accusés (§3.6 — titulaire / manager seulement) ─────────────

/** Qui a accusé, qui a seulement vu, qui n'a rien vu. Ni IP ni user-agent. */
export const accuses = (id) => api.get(`${BASE}/${id}/accuses`).then(unwrap);

/** L'URL d'export CSV du tableau (idiome `export.controller.js`). */
export const accusesCsvUrl = (id) => `${BASE}/${id}/accuses.csv`;

export default {
  compteur,
  actives,
  marquerVu,
  accuser,
  creer,
  lister,
  detail,
  modifier,
  supprimer,
  apercu,
  envoyer,
  annuler,
  reemettre,
  accuses,
  accusesCsvUrl,
};
